import React, { useState, useEffect } from 'react';
import './CookieConsent.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    // Initialize Google Consent Mode v2 with default denied state
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;

    // Set default consent state before loading tags
    gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied'
    });

    // Load Google Analytics immediately (required for detection)
    loadGoogleAnalytics();

    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');

    if (consent === null) {
      // No choice made yet, show banner
      setShowBanner(true);
    } else {
      // User has made a choice, update consent mode
      const consentData = JSON.parse(consent);
      if (consentData.analytics) {
        // Update consent to granted
        gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'analytics_storage': 'granted'
        });
        loadLinkedInInsightTag();
      }
    }

    // Listen for custom event to reopen banner from footer
    const handleReopenBanner = () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent) {
        const consentData = JSON.parse(consent);
        setAnalyticsEnabled(consentData.analytics || false);
      }
      setShowBanner(true);
      setShowSettings(true);
    };

    window.addEventListener('openCookieSettings', handleReopenBanner);

    return () => {
      window.removeEventListener('openCookieSettings', handleReopenBanner);
    };
  }, []);

  const clearGoogleAnalyticsCookies = () => {
    // List of Google Analytics cookie names to clear
    const gaCookies = ['_ga', '_gat', '_gid', '_gcl_au', '_gac_', '_gat_gtag_'];

    // Clear cookies for current domain
    gaCookies.forEach(cookieName => {
      // Delete for current domain
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      // Delete for root domain
      const domain = window.location.hostname;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain}`;
    });

    // Clear all cookies that start with _ga or _gac
    const allCookies = document.cookie.split(';');
    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.startsWith('_ga') || cookieName.startsWith('_gac') || cookieName.startsWith('_gat')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        const domain = window.location.hostname;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain}`;
      }
    });
  };

  const loadGoogleAnalytics = () => {
    // Check if already loaded
    if (document.querySelector('script[src*="gtag/js"]')) {
      return;
    }

    // Load Google Analytics script
    const script = document.createElement('script');
    script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17787661982';
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', 'AW-17787661982');
    };
  };

  const loadLinkedInInsightTag = () => {
    // Check if already loaded
    if (window._linkedin_data_partner_ids && window._linkedin_data_partner_ids.includes('8396012')) {
      return;
    }

    // Set LinkedIn partner ID
    window._linkedin_partner_id = '8396012';
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);

    // Load LinkedIn Insight Tag script
    if (!window.lintrk) {
      window.lintrk = function(a, b) { window.lintrk.q.push([a, b]); };
      window.lintrk.q = [];
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    document.getElementsByTagName('head')[0].appendChild(script);
  };

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));

    // Update Google Consent Mode to granted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted'
      });
    }

    loadLinkedInInsightTag();
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    // Clear existing Google Analytics cookies
    clearGoogleAnalyticsCookies();

    const consent = {
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    const consent = {
      necessary: true,
      analytics: analyticsEnabled,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));

    if (analyticsEnabled) {
      // Update Google Consent Mode to granted
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'analytics_storage': 'granted'
        });
      }
      loadLinkedInInsightTag();
    } else {
      // Update Google Consent Mode to denied
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'analytics_storage': 'denied'
        });
      }
      // Clear Google Analytics cookies if user disabled analytics
      clearGoogleAnalyticsCookies();
    }

    setShowBanner(false);
    setShowSettings(false);
  };

  const handleManageSettings = () => {
    // Load current consent settings
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      const consentData = JSON.parse(consent);
      setAnalyticsEnabled(consentData.analytics || false);
    }
    setShowSettings(true);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="cookie-consent-wrapper">
      <div className="cookie-consent-banner">
        {!showSettings ? (
          // Main banner view
          <div className="cookie-consent-content">
            <div className="cookie-consent-text">
              <h3>We Value Your Privacy</h3>
              <p>
                We use cookies to enhance your browsing experience and analyze site traffic.
                Essential cookies are always active. You can choose to accept or decline optional cookies.
                {' '}
                <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer">
                  Learn more in our Privacy Policy
                </a>
              </p>
            </div>
            <div className="cookie-consent-actions">
              <button
                className="cookie-btn cookie-btn-settings"
                onClick={handleManageSettings}
              >
                Manage Settings
              </button>
              <button
                className="cookie-btn cookie-btn-reject"
                onClick={handleRejectAll}
              >
                Reject Optional
              </button>
              <button
                className="cookie-btn cookie-btn-accept"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          // Settings view
          <div className="cookie-consent-settings">
            <div className="cookie-settings-header">
              <h3>Cookie Settings</h3>
              <button
                className="cookie-close-btn"
                onClick={() => setShowSettings(false)}
                aria-label="Close settings"
              >
                ×
              </button>
            </div>

            <div className="cookie-settings-body">
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h4>Necessary Cookies</h4>
                    <p className="cookie-category-description">
                      Required for the website to function properly. These cannot be disabled.
                    </p>
                  </div>
                  <label className="cookie-toggle disabled">
                    <input type="checkbox" checked disabled />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
                <div className="cookie-details">
                  <p><strong>Used for:</strong> Session management, security, and basic site functionality</p>
                  <p><strong>Cookies:</strong> Session cookies, CSRF tokens</p>
                </div>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h4>Analytics Cookies (Google Analytics & LinkedIn)</h4>
                    <p className="cookie-category-description">
                      Help us understand how visitors interact with our website by collecting anonymous data.
                    </p>
                  </div>
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={analyticsEnabled}
                      onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
                <div className="cookie-details">
                  <p><strong>Used for:</strong> Website analytics, conversion tracking, user behavior analysis, B2B audience insights</p>
                  <p><strong>Cookies:</strong> _ga, _gat, _gid, _gcl_au (Google Analytics/Ads), li_* (LinkedIn Insight Tag)</p>
                  <p><strong>Providers:</strong> Google LLC, LinkedIn Corporation</p>
                  <p><strong>Duration:</strong> Up to 2 years</p>
                </div>
              </div>

              <div className="cookie-category cookie-category-info">
                <div className="cookie-category-header">
                  <div>
                    <h4>Privacy-Friendly Analytics (Plausible)</h4>
                    <p className="cookie-category-description">
                      We use Plausible Analytics which doesn't use cookies and is GDPR-compliant by default.
                    </p>
                  </div>
                  <div className="cookie-info-badge">
                    <span>✓ No Cookies</span>
                  </div>
                </div>
                <div className="cookie-details">
                  <p><strong>Used for:</strong> Anonymous traffic statistics without tracking</p>
                  <p><strong>Cookies:</strong> None - completely cookie-free</p>
                  <p><strong>Always Active:</strong> Respects your privacy by design</p>
                </div>
              </div>
            </div>

            <div className="cookie-settings-footer">
              <button
                className="cookie-btn cookie-btn-secondary"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
              <button
                className="cookie-btn cookie-btn-accept"
                onClick={handleSaveSettings}
              >
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
